import * as turf from '@turf/turf';
import { Feature, MultiPolygon, Point, Polygon, Properties } from '@turf/turf';
import {
  PRESCRIPTION_INTERVAL_COLORS,
  PRESCRIPTION_NULL_INTERVAL_COLOR,
} from '../../constants/PrescriptionConstants';
import type Bounds from '../../model/Bounds';
import type Field from '../../model/Field';
import type { PolygonCoordinates } from '../../../types/geo';
import type { Interval, PrescriptionJob } from './PrescriptionJob';
import type { LayerValueGrid } from './SeedingJob/SeedingJobCommonTypes';

export type NullZoneCell = { x: number; y: number };

// This value is outside the source values used by prescription intervals.
export const NULL_INTERVAL_VALUE = -1;
export const MIN_NULL_ZONE_AREA_FRACTION = 0.02;

export const isNullInterval = (interval: Interval): boolean =>
  interval.min === NULL_INTERVAL_VALUE && interval.max === NULL_INTERVAL_VALUE;

export const getPrescriptionIntervalColor = (
  interval: Interval,
  index: number,
  intervals: Array<Interval>
) => {
  if (isNullInterval(interval)) return PRESCRIPTION_NULL_INTERVAL_COLOR;
  return PRESCRIPTION_INTERVAL_COLORS[index - (isNullInterval(intervals[0]) ? 1 : 0)];
};

export const getNullIntervalLayout = (intervals: Array<Interval>) => {
  if (!isNullInterval(intervals[0]) || intervals.length < 2) {
    return { nullWidth: 0, normalWidth: 100, normalMin: intervals[0].min };
  }

  // Keep this independent of interval boundaries, which users can drag.
  const nullWidth = 100 / intervals.length;

  return { nullWidth, normalWidth: 100 - nullWidth, normalMin: intervals[1].min };
};

export const isCellInsideField = (
  x: number,
  y: number,
  values: LayerValueGrid,
  bounds: Bounds,
  polygon: Feature<Polygon | MultiPolygon> | Polygon | MultiPolygon
): boolean => {
  const height = values.length;
  const width = values[0].length;
  const lng = bounds.west + ((x + 0.5) / width) * (bounds.east - bounds.west);
  const lat = bounds.north - ((y + 0.5) / height) * (bounds.north - bounds.south);
  return turf.booleanPointInPolygon(turf.point([lng, lat]), polygon);
};

// Group neighboring null cells so a hover selects one continuous part of the field.
export const getNullValueRegions = (
  values: LayerValueGrid,
  isInsideField: (x: number, y: number) => boolean = () => true
) => {
  const height = values.length;
  const width = values[0].length;
  const regionIds = Array.from({ length: height }, () => Array(width).fill(-1));
  const regions: NullZoneCell[][] = [];

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (values[y][x] !== null || regionIds[y][x] !== -1 || !isInsideField(x, y)) continue;

      const id = regions.length;
      const cells: NullZoneCell[] = [];
      const pending: NullZoneCell[] = [{ x, y }];
      regionIds[y][x] = id;

      while (pending.length) {
        const cell = pending.pop();
        if (!cell) break;
        cells.push(cell);
        for (const neighbor of [
          { x: cell.x - 1, y: cell.y },
          { x: cell.x + 1, y: cell.y },
          { x: cell.x, y: cell.y - 1 },
          { x: cell.x, y: cell.y + 1 },
        ]) {
          const { x: nx, y: ny } = neighbor;
          if (
            ny >= 0 &&
            ny < height &&
            nx >= 0 &&
            nx < width &&
            values[ny][nx] === null &&
            regionIds[ny][nx] === -1 &&
            isInsideField(nx, ny)
          ) {
            regionIds[ny][nx] = id;
            pending.push(neighbor);
          }
        }
      }
      regions.push(cells);
    }
  }

  return { regionIds, regions };
};

export const fillNullValuesInsideField = (
  values: LayerValueGrid,
  bounds: Bounds,
  coordinates: PolygonCoordinates
): LayerValueGrid => {
  const polygon = turf.polygon(coordinates);
  const { regions } = getNullValueRegions(values, (x, y) =>
    isCellInsideField(x, y, values, bounds, polygon)
  );
  if (regions.length === 0) return values;
  const nextValues = values.map((row) => row.slice());
  regions.forEach((region) =>
    region.forEach(({ x, y }) => {
      nextValues[y][x] = NULL_INTERVAL_VALUE;
    })
  );
  return nextValues;
};

// Returning to bulk overview removes the extra interval, so its pixels must
// become selectable null cells again.
export const restoreNullValues = (values: LayerValueGrid): LayerValueGrid =>
  values.map((row) => row.map((value) => (value === NULL_INTERVAL_VALUE ? null : value)));

export const getNullZoneAreaForField = (
  job: PrescriptionJob | null | undefined,
  field: Field | null | undefined
) => {
  if (!job || !field || String(job.fieldId) !== String(field.fieldId)) {
    return { nullArea: 0, fieldArea: 0 };
  }
  const fieldArea = Number(job.fieldSize != null ? job.fieldSize : field.size);
  if (!Number.isFinite(fieldArea) || fieldArea <= 0) return { nullArea: 0, fieldArea: 0 };

  const values = job.values;
  if (
    !Array.isArray(values) ||
    values.length === 0 ||
    !Array.isArray(values[0]) ||
    !values[0].length
  ) {
    return { nullArea: 0, fieldArea: 0 };
  }
  const polygon = turf.polygon(field.polygon.coordinates);
  let fieldCells = 0;
  let nullCells = 0;
  for (let y = 0; y < values.length; y++) {
    for (let x = 0; x < values[y].length; x++) {
      if (!isCellInsideField(x, y, values, field.bounds, polygon)) continue;
      fieldCells++;
      if (values[y][x] === null || values[y][x] === NULL_INTERVAL_VALUE) nullCells++;
    }
  }
  // Cells have equal area within a job, so use their share of in-field cells
  // to estimate the null area from the recorded field area.
  return { nullArea: fieldCells ? (fieldArea * nullCells) / fieldCells : 0, fieldArea };
};

export const hasSignificantNullZonesInField = (
  job: PrescriptionJob | null | undefined,
  field: Field | null | undefined
): boolean => {
  const { nullArea, fieldArea } = getNullZoneAreaForField(job, field);
  return fieldArea > 0 && nullArea > fieldArea * MIN_NULL_ZONE_AREA_FRACTION;
};

export const getBrushablePixels = (
  values: LayerValueGrid,
  bounds: Bounds,
  coordinates: PolygonCoordinates,
  brushPolygon: Polygon | MultiPolygon | Feature<Polygon | MultiPolygon>
): NullZoneCell[] => {
  const fieldPolygon = turf.polygon(coordinates);
  const pixels: NullZoneCell[] = [];
  const height = values.length;
  const width = values[0].length;
  const [west, south, east, north] = turf.bbox(brushPolygon);
  const longitudeSpan = bounds.east - bounds.west;
  const latitudeSpan = bounds.north - bounds.south;
  const firstX = Math.max(0, Math.floor(((west - bounds.west) / longitudeSpan) * width - 0.5));
  const lastX = Math.min(
    width - 1,
    Math.ceil(((east - bounds.west) / longitudeSpan) * width - 0.5)
  );
  const firstY = Math.max(0, Math.floor(((bounds.north - north) / latitudeSpan) * height - 0.5));
  const lastY = Math.min(
    height - 1,
    Math.ceil(((bounds.north - south) / latitudeSpan) * height - 0.5)
  );

  for (let y = firstY; y <= lastY; y++) {
    for (let x = firstX; x <= lastX; x++) {
      const value = values[y][x];
      if (!((value !== null && value > 0) || value === null || value === NULL_INTERVAL_VALUE))
        continue;

      const lng: number = bounds.west + ((x + 0.5) / width) * (bounds.east - bounds.west);
      const lat: number = bounds.north - ((y + 0.5) / height) * (bounds.north - bounds.south);
      const point: Feature<Point, Properties> = turf.point([lng, lat]);
      if (
        turf.booleanPointInPolygon(point, fieldPolygon) &&
        turf.booleanPointInPolygon(point, brushPolygon)
      ) {
        pixels.push({ x, y });
      }
    }
  }

  return pixels;
};
